import { createContext, FC, ReactNode, useMemo } from "react";

import OverlayLoading from "src/components/DataStates/OverlayLoading.tsx";
import PageError from "src/components/Errors/PageError.tsx";
import { GlobalErrorLayout } from "src/errors/GlobalErrorLayout.tsx";
import { Scope as ApiScope, Resource } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { useGetPermissionsQuery } from "src/graphql/anti-fraud/queries/permissions/GetPermissionsQuery.ts";

export enum ScopeResource {
  ALERT = "ALERT",
  BLACKLIST_ELEMENT = "BLACKLIST_ELEMENT",
  CASE = "CASE",
  CONFIGURATION = "CONFIGURATION",
  IMPORT = "IMPORT",
  RULE = "RULE",
  SCENARIO = "SCENARIO",
  SCENARIO_EXECUTION_RESULT = "SCENARIO_EXECUTION_RESULT",
  DOCUMENTS_STORAGE = "DOCUMENTS_STORAGE",
  GDPR = "GDPR",
}

export enum Scope {
  ADD_IDENTIFIER = "ADD_IDENTIFIER",
  ASSIGN = "ASSIGN",
  CREATE = "CREATE",
  DELETE = "DELETE",
  DISABLE = "DISABLE",
  EDIT = "EDIT",
  EXECUTE = "EXECUTE",
  IMPORT = "IMPORT",
  UNASSIGN = "UNASSIGN",
  VALIDATE = "VALIDATE",
  VIEW = "VIEW",
  ADVANCED = "ADVANCED",
  EXPORT = "EXPORT",
}

export type Permission = {
  resource: ScopeResource;
  scopes: Scope[];
};

export type PermissionsContextType = {
  hasPermission: (resource: ScopeResource, ...scopes: Scope[]) => boolean;
  hasPermissions: (permissions: Permission[]) => boolean;
  hasPartialPermission: (resource: ScopeResource, ...scopes: Scope[]) => boolean;
  hasPartialPermissions: (permissions: Permission[]) => boolean;
};

export type PermissionMap = {
  [key in ScopeResource]: Scope[];
};

const defaultScopeMap = Object.values(ScopeResource).reduce((acc, resource) => {
  acc[resource] = [];
  return acc;
}, {} as PermissionMap);

export const PermissionsContext = createContext<PermissionsContextType>({
  hasPermission: () => false,
  hasPermissions: () => false,
  hasPartialPermission: () => false,
  hasPartialPermissions: () => false,
});

export const PermissionsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data, error, loading, hasError } = useGetPermissionsQuery();

  const permissionMap: PermissionMap = useMemo(() => {
    return data.reduce((acc, { resource, scopes }) => {
      acc[mapScopeResource(resource)] = scopes.map(mapScope);
      return acc;
    }, defaultScopeMap);
  }, [data]);

  const contextValue = useMemo<PermissionsContextType>(() => {
    const hasPermission = (resource: ScopeResource, ...scopes: Scope[]) =>
      scopes.every((scope) => permissionMap[resource].includes(scope));

    const hasPermissions = (list: Permission[]) =>
      list.every(({ resource, scopes }) => hasPermission(resource, ...scopes));

    const hasPartialPermission = (resource: ScopeResource, ...scopes: Scope[]) =>
      scopes.some((scope) => permissionMap[resource].includes(scope));

    const hasPartialPermissions = (list: Permission[]) =>
      list.some(({ resource, scopes }) => hasPartialPermission(resource, ...scopes));

    return {
      hasPermission,
      hasPermissions,
      hasPartialPermission,
      hasPartialPermissions,
    };
  }, [permissionMap]);

  if (loading) {
    return <OverlayLoading />;
  }

  if (hasError) {
    return (
      <GlobalErrorLayout>
        <PageError title={error?.title} description={error?.message ?? ""} />
      </GlobalErrorLayout>
    );
  }

  return <PermissionsContext.Provider value={contextValue}>{children}</PermissionsContext.Provider>;
};

const scopeMap: { [key in ApiScope]: Scope } = {
  addIdentifier: Scope.ADD_IDENTIFIER,
  assign: Scope.ASSIGN,
  advanced: Scope.ADVANCED,
  create: Scope.CREATE,
  delete: Scope.DELETE,
  disable: Scope.DISABLE,
  edit: Scope.EDIT,
  execute: Scope.EXECUTE,
  import: Scope.IMPORT,
  unassign: Scope.UNASSIGN,
  validate: Scope.VALIDATE,
  view: Scope.VIEW,
  export: Scope.EXPORT,
};

const mapScope = (apiScope: ApiScope): Scope => {
  return scopeMap[apiScope];
};

const resourceMap: { [key in Resource]: ScopeResource } = {
  Alert: ScopeResource.ALERT,
  BlacklistElement: ScopeResource.BLACKLIST_ELEMENT,
  Case: ScopeResource.CASE,
  Configuration: ScopeResource.CONFIGURATION,
  Import: ScopeResource.IMPORT,
  Rule: ScopeResource.RULE,
  Scenario: ScopeResource.SCENARIO,
  ScenarioExecutionResult: ScopeResource.SCENARIO_EXECUTION_RESULT,
  DocumentsStorage: ScopeResource.DOCUMENTS_STORAGE,
  GDPR: ScopeResource.GDPR,
};

const mapScopeResource = (apiResource: Resource): ScopeResource => {
  return resourceMap[apiResource];
};
